import { useEffect, useState, useCallback, useMemo } from "react";
import styles from "./revenueReports.module.css";
import { AdminHeader } from "../../../components/adminHeader/adminHeader";
import AdminAPI from "../../../api/AdminAPI";
import { downloadFile } from "../../../utils/downloadFile";
import { useParams } from "react-router";
import { useConfirmPopup } from "../../../components/popup/confirmPopup/confirmPopup";
import { usePopup } from "../../../components/popup/popup";
import { Button } from "../../../components/button/button";
import { Separator } from "../../../components/separator/separator";

import { Text } from "../../../components/text/text";

const months = [
    "январь", "февраль", "март", "апрель", "май", "июнь",
    "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь"
]

export const RevenueReportsPage = () => {
    const { cafeId } = useParams();
    const [reports, setReports] = useState([]);
    const [regeneratingId, setRegeneratingId] = useState(null);
    const [forRegenerate, setForRegenerate] = useState(null);
    const [generating, setGenerating] = useState(false);

    const now = new Date();
    const defaultMonth = now.getMonth() === 0 ? 12 : now.getMonth();
    const defaultYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
    const [selectedYear, setSelectedYear] = useState(defaultYear);

    const loadReports = useCallback(() => {
        AdminAPI
            .fetchRevenueReportsList(cafeId)
            .then(setReports)
            .catch(console.error);
    }, [cafeId]);

    useEffect(() => {
        loadReports();
    }, [loadReports]);

    const handleDownload = (report) => {
        AdminAPI
            .fetchRevenueReportDownloadLink(report.id, cafeId)
            .then((res) => downloadFile(res, report.originalFilename))
            .catch(console.error);
    }

    const handleRegenerate = useCallback(() => {
        if (!forRegenerate) return;
        const { id, reportMonth, reportYear } = forRegenerate;
        setRegeneratingId(id);
        AdminAPI
            .regenerateRevenueReport(cafeId, reportMonth, reportYear)
            .then((res) => {
                loadReports();
                downloadFile(res.url, res.filename);
            })
            .catch(console.error)
            .finally(() => setRegeneratingId(null));
    }, [forRegenerate, cafeId, loadReports]);

    const [RegeneratePopup, openRegeneratePopup] = useConfirmPopup(
        "Подтвердите действие",
        "Вы уверены, что хотите перегенерировать отчет? Старый отчет будет удалён.",
        handleRegenerate,
    );

    const onRegenerateClick = (e, report) => {
        e.stopPropagation();
        setForRegenerate(report);
        openRegeneratePopup();
    };

    const [GeneratePopup, openGeneratePopup, closeGeneratePopup] = usePopup();

    const yearOptions = useMemo(() => {
        const current = new Date().getFullYear();
        return [current - 1, current];
    }, []);

    const handleGenerate = () => {
        setGenerating(true);
        closeGeneratePopup();
        AdminAPI
            .regenerateRevenueReport(cafeId, selectedMonth, selectedYear)
            .then((res) => {
                loadReports();
                downloadFile(res.url, res.filename);
            })
            .catch(console.error)
            .finally(() => setGenerating(false));
    };

    return (
        <>
            <AdminHeader title="Отчеты по выручке" />

            {
                reports.length > 0 ?
                <div className={styles.reports}>
                    {
                        reports.map(file => (
                            <div
                                key={file.id}
                                className={styles.file}
                                onClick={() => handleDownload(file)}
                            >
                                <div className={styles.file_top_row}>
                                    <Text as="div" variant="body" size={16} weight={700}>
                                        Отчет
                                    </Text>
                                    <button
                                        className={styles.regenerate_btn}
                                        disabled={regeneratingId === file.id}
                                        onClick={(e) => onRegenerateClick(e, file)}
                                        title="Перегенерировать отчет"
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C15.3313 3 18.2398 4.80905 19.8256 7.5M19.8256 7.5V3M19.8256 7.5H15.3256"
                                                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                </div>
                                <Text as="div" variant="body" className={styles.file_date} >
                                    {months[file.reportMonth - 1]} {file.reportYear}
                                </Text>
                                <Text as="div" variant="body" className={styles.file_type} size={40} weight={900}>
                                    XLSX
                                </Text>
                            </div>
                        ))
                    }
                </div>
                :
                <Text as="div" variant="body" className={styles.no_reports} size={16} color="var(--app-secondary-text-color)" >
                    Отчетов пока нет...
                </Text>
            }

            <Separator />

            <Button
                fixed
                text={generating ? "Генерация..." : "Сгенерировать отчет"}
                disabled={generating}
                onClick={openGeneratePopup}
            />

            <GeneratePopup>
                <Text as="div" variant="title" align="center">
                    Сгенерировать отчет
                </Text>

                <div className={styles.generate_form}>
                    <label className={styles.generate_label}>
                        <Text as="span" variant="body" size={14}>Месяц</Text>
                        <select
                            className={styles.generate_select}
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(Number(e.target.value))}
                        >
                            {months.map((name, i) => (
                                <option key={i + 1} value={i + 1}>{name}</option>
                            ))}
                        </select>
                    </label>

                    <label className={styles.generate_label}>
                        <Text as="span" variant="body" size={14}>Год</Text>
                        <select
                            className={styles.generate_select}
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                        >
                            {yearOptions.map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </label>
                </div>

                <Button
                    text="Сгенерировать"
                    onClick={handleGenerate}
                />
            </GeneratePopup>

            {RegeneratePopup}
        </>
    );
}